import React from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

const rootPath = '$';
const nestedCollapseDepth = 2;

function isContainer(value) {
  return value !== null && typeof value === 'object';
}

function valueType(value) {
  if (Array.isArray(value)) {
    return 'array';
  }
  if (value === null) {
    return 'null';
  }
  return typeof value;
}

function childPath(path, key) {
  return `${path}/${String(key).replace(/~/g, '~0').replace(/\//g, '~1')}`;
}

function getEntries(value) {
  if (Array.isArray(value)) {
    return value.map((item, index) => [index, item]);
  }
  if (isContainer(value)) {
    return Object.entries(value);
  }
  return [];
}

function collectContainerPaths(value, path = rootPath, depth = 0, acc = []) {
  if (!isContainer(value)) {
    return acc;
  }
  acc.push({ path, depth });
  getEntries(value).forEach(([key, child]) => {
    collectContainerPaths(child, childPath(path, key), depth + 1, acc);
  });
  return acc;
}

function formatPrimitive(value) {
  if (typeof value === 'string') {
    return JSON.stringify(value);
  }
  if (typeof value === 'undefined') {
    return 'undefined';
  }
  return String(value);
}

function typeColor(type) {
  switch (type) {
    case 'string':
      return 'success.main';
    case 'number':
      return 'primary.main';
    case 'boolean':
      return 'secondary.main';
    case 'null':
    case 'undefined':
      return 'text.disabled';
    default:
      return 'text.primary';
  }
}

function containerSummary(value) {
  const count = getEntries(value).length;
  if (Array.isArray(value)) {
    return `${count} item${count === 1 ? '' : 's'}`;
  }
  return `${count} key${count === 1 ? '' : 's'}`;
}

function openingToken(value) {
  return Array.isArray(value) ? '[' : '{';
}

function closingToken(value) {
  return Array.isArray(value) ? ']' : '}';
}

function NodeLabel({ name, isRoot }) {
  if (isRoot) {
    return (
      <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
        root
      </Typography>
    );
  }
  const label = typeof name === 'number' ? `[${name}]` : JSON.stringify(name);
  return (
    <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 650 }}>
      {label}
    </Typography>
  );
}

function JsonNode({ name, value, path, depth, collapsedPaths, onToggle, isRoot = false }) {
  const type = valueType(value);
  const isNodeContainer = isContainer(value);
  const collapsed = collapsedPaths.has(path);
  const entries = getEntries(value);
  const paddingLeft = depth * 2;

  if (!isNodeContainer) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 0.75,
          minHeight: 28,
          pl: paddingLeft,
          pr: 1,
        }}
      >
        <Box sx={{ width: 32, flexShrink: 0 }} />
        <NodeLabel name={name} isRoot={isRoot} />
        <Typography component="span" variant="body2" color="text.secondary">
          :
        </Typography>
        <Typography
          component="span"
          variant="body2"
          sx={{
            color: typeColor(type),
            fontFamily: 'Monaco, Menlo, Consolas, "Courier New", monospace',
            overflowWrap: 'anywhere',
          }}
        >
          {formatPrimitive(value)}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          minHeight: 32,
          pl: paddingLeft,
          pr: 1,
        }}
      >
        <Tooltip title={collapsed ? 'Expand node' : 'Collapse node'}>
          <IconButton
            size="small"
            onClick={() => onToggle(path)}
            aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${isRoot ? 'root' : String(name)}`}
            aria-expanded={!collapsed}
            sx={{ width: 32, height: 32 }}
          >
            {collapsed ? <KeyboardArrowRightIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <NodeLabel name={name} isRoot={isRoot} />
        <Typography component="span" variant="body2" color="text.secondary">
          :
        </Typography>
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontFamily: 'Monaco, Menlo, Consolas, "Courier New", monospace',
            fontWeight: 700,
          }}
        >
          {collapsed ? `${openingToken(value)}...${closingToken(value)}` : openingToken(value)}
        </Typography>
        <Chip size="small" label={containerSummary(value)} />
      </Box>
      {collapsed ? null : (
        <>
          {entries.map(([key, child]) => (
            <JsonNode
              key={childPath(path, key)}
              name={key}
              value={child}
              path={childPath(path, key)}
              depth={depth + 1}
              collapsedPaths={collapsedPaths}
              onToggle={onToggle}
            />
          ))}
          <Box sx={{ pl: paddingLeft + 4, minHeight: 24 }}>
            <Typography
              component="span"
              variant="body2"
              sx={{
                fontFamily: 'Monaco, Menlo, Consolas, "Courier New", monospace',
                fontWeight: 700,
              }}
            >
              {closingToken(value)}
            </Typography>
          </Box>
        </>
      )}
    </>
  );
}

function parseRawData(rawData) {
  if (rawData === null || typeof rawData === 'undefined' || rawData === '') {
    return { value: null, empty: true, parseError: null, formatted: '' };
  }
  if (typeof rawData !== 'string') {
    return {
      value: rawData,
      empty: false,
      parseError: null,
      formatted: JSON.stringify(rawData, null, 2),
    };
  }
  const trimmed = rawData.trim();
  if (!trimmed) {
    return { value: null, empty: true, parseError: null, formatted: '' };
  }
  try {
    const parsed = JSON.parse(trimmed);
    return {
      value: parsed,
      empty: false,
      parseError: null,
      formatted: JSON.stringify(parsed, null, 2),
    };
  } catch (err) {
    return {
      value: trimmed,
      empty: false,
      parseError: err,
      formatted: trimmed,
    };
  }
}

const JsonTreeViewer = ({ data }) => {
  const parsed = React.useMemo(() => parseRawData(data), [data]);
  const containerPaths = React.useMemo(
    () => collectContainerPaths(parsed.value),
    [parsed.value],
  );
  const [collapsedPaths, setCollapsedPaths] = React.useState(() => new Set());
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    const initiallyCollapsed = containerPaths
      .filter((item) => item.depth >= nestedCollapseDepth)
      .map((item) => item.path);
    setCollapsedPaths(new Set(initiallyCollapsed));
    setCopied(false);
  }, [containerPaths]);

  const togglePath = React.useCallback((path) => {
    setCollapsedPaths((previous) => {
      const next = new Set(previous);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const expandAll = React.useCallback(() => {
    setCollapsedPaths(new Set());
  }, []);

  const collapseAll = React.useCallback(() => {
    setCollapsedPaths(new Set(containerPaths.map((item) => item.path)));
  }, [containerPaths]);

  const copyJSON = React.useCallback(async () => {
    if (!parsed.formatted || typeof navigator === 'undefined' || !navigator.clipboard) {
      return;
    }
    await navigator.clipboard.writeText(parsed.formatted);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }, [parsed.formatted]);

  if (parsed.empty) {
    return (
      <Box
        sx={{
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No data available.
        </Typography>
      </Box>
    );
  }

  if (parsed.parseError) {
    return (
      <Box
        component="pre"
        sx={{
          flex: 1,
          bgcolor: 'background.paper',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 2,
          fontFamily: 'Monaco, Menlo, Consolas, "Courier New", monospace',
          fontSize: 12,
          overflowX: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: 'calc(100vh - 260px)',
          m: 0,
        }}
      >
        {parsed.formatted}
      </Box>
    );
  }

  return (
    <Stack
      spacing={1}
      sx={{
        flex: 1,
        minHeight: 0,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2">JSON</Typography>
          {isContainer(parsed.value) ? <Chip size="small" label={containerSummary(parsed.value)} /> : null}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="outlined"
            startIcon={<UnfoldMoreIcon />}
            onClick={expandAll}
            disabled={containerPaths.length === 0}
          >
            Expand all
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<UnfoldLessIcon />}
            onClick={collapseAll}
            disabled={containerPaths.length === 0}
          >
            Collapse all
          </Button>
          <Tooltip title={copied ? 'Copied' : 'Copy JSON'}>
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  copyJSON().catch(() => {});
                }}
                aria-label="Copy JSON"
                disabled={!parsed.formatted}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          maxHeight: 'calc(100vh - 260px)',
          overflow: 'auto',
          bgcolor: 'background.paper',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          py: 1,
        }}
      >
        <JsonNode
          name="root"
          value={parsed.value}
          path={rootPath}
          depth={0}
          collapsedPaths={collapsedPaths}
          onToggle={togglePath}
          isRoot
        />
      </Box>
    </Stack>
  );
};

export default JsonTreeViewer;
